// Dictation (speech-to-text), using the browser's own ON-DEVICE recognition.
//
// STRICTLY LOCAL, AND THAT IS ENFORCED BY THE BROWSER RATHER THAN TRUSTED.
// `processLocally = true` is set on every session created in this file and there
// is no code path here that sets it false or leaves it unset. This matters
// because the DEFAULT behaviour of this API is the opposite: with the flag
// absent, Chrome and Edge stream microphone audio to a remote vendor service, so
// a dictated patient name would leave the machine (Finding #108). The plan
// document originally claimed the "nothing leaves the browser" property for both
// halves of the Web Speech API; it holds for read-aloud in speech.js and NOT for
// this half, which is why the guarantee here is a flag and a pre-flight check
// rather than a comment.
//
// MEASURED, not assumed (Finding #114): with processLocally = true and on-device
// recognition unavailable, the browser REFUSES — it raises `language-not-supported`
// rather than quietly falling back to the cloud. So the flag is an enforcement.
// That is the whole basis for this file being safe on a medical tenant.
//
// WHERE IT WORKS. Chromium 139+ exposes the on-device mode; the language packs
// are delivered by Google's component updater, so as of 2026-08-24 Chrome reports
// en-US as `downloadable` while EDGE reports `unavailable` even with the SODA
// component installed — the API surface is inherited, the binding is not. Nothing
// here breaks on Edge: availability() returns 'unavailable' and the caller does
// not render a button at all, exactly as speech.js does for read-aloud.
//
// ARABIC IS NOT AVAILABLE THROUGH THIS ROUTE AT ALL and no amount of retrying
// will change it — Chrome's on-device list is 17 languages and Arabic is not one
// of them (Finding #115). Arabic dictation waits for the self-hosted engine
// planned for the new pod (reminder #22, Finding #116). Hence the `lang`
// parameter throughout: the interface is the one the second backend will reuse,
// so adding it later is a new module behind these same five functions, not a
// rewrite of the page.

// Only en-US is offered today. Kept as a parameter rather than inlined so the
// call sites are already language-aware when a second language becomes possible.
const DEFAULT_LANG = 'en-US';

// A session is capped rather than left to run. `continuous = true` means a
// forgotten microphone stays open indefinitely, and an open microphone on a
// clinical workstation is not a small thing.
const MAX_SESSION_MS = 120000;

// Chrome can end a recognition session on a PAUSE even with continuous = true.
// Restarting transparently is what stops dictation dying mid-thought, and it is
// the single most common reason these features feel broken. It is bounded because
// a browser that ends immediately and repeatedly would otherwise spin forever.
const MAX_SILENT_RESTARTS = 3;

function ctor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * Does this browser expose the ON-DEVICE API at all?
 *
 * The plain constructor is deliberately NOT enough to return true: it has existed
 * for years as the cloud-backed implementation, and treating its presence as
 * support is exactly how audio ends up leaving the device. The static available()
 * and install() methods arrived WITH the on-device mode, so requiring them is the
 * cheapest honest test that the local path exists.
 */
function isSupported() {
  const SR = ctor();
  return !!SR && typeof SR.available === 'function' && typeof SR.install === 'function';
}

/**
 * 'available' | 'downloadable' | 'downloading' | 'unavailable'
 *
 * Asked BEFORE any microphone is opened, so the UI can decide whether to offer a
 * button instead of discovering the answer from a failed start(). Any throw is
 * reported as 'unavailable': a capability check that itself errors is not a
 * capability.
 */
async function availability(lang = DEFAULT_LANG) {
  const SR = ctor();
  if (!SR || typeof SR.available !== 'function') return 'unavailable';
  try {
    return await SR.available({ langs: [lang], processLocally: true });
  } catch {
    return 'unavailable';
  }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wait out a 'downloading' state and report where it landed.
 *
 * 'downloading' IS A TRANSIENT STATE WITH NO EVENT TO LISTEN FOR — the API offers
 * no progress callback and no promise for a download someone else started, so
 * polling availability() is not laziness, it is the only interface there is.
 *
 * Every wait here is bounded. An unbounded wait on this state is what put a
 * spinner on screen forever on 2026-08-24 (see Plan #50.design.downloading_is_not_
 * downloadable), and 'the pack never arrived' has to be a reportable outcome
 * rather than a hang.
 *
 * Resolves to the final state string, never throws.
 */
async function settle(lang = DEFAULT_LANG, { timeoutMs = 120000, pollMs = 2000, onState, isCancelled } = {}) {
  let state = await availability(lang);
  const deadline = Date.now() + timeoutMs;

  while (state === 'downloading' && Date.now() < deadline) {
    if (typeof isCancelled === 'function' && isCancelled()) return state;
    if (typeof onState === 'function') onState(state);
    await sleep(pollMs);
    state = await availability(lang);
  }

  if (typeof onState === 'function') onState(state);
  return state;
}

/**
 * Make the on-device pack usable if it can be, and report the final state:
 * 'available' | 'downloading' | 'downloadable' | 'unavailable'.
 *
 * Returns a STATE STRING rather than a boolean, because the three failures are
 * not interchangeable: 'unavailable' means never, 'downloading' means not yet and
 * worth waiting, 'downloadable' means the browser declined to fetch it now.
 *
 * install() needs a user gesture, so this must be called from a click and never
 * from an effect on mount.
 *
 * TWO THINGS LEARNED THE HARD WAY, both on 2026-08-24:
 *  - install() IS ONLY CALLED FOR 'downloadable'. Calling it while a download is
 *    already in flight is at best redundant and was the actual cause of the
 *    forever-spinner.
 *  - install() IS RACED AGAINST THE TIMEOUT, not awaited blindly. It need not
 *    resolve until the entire pack has arrived, and an unresolved promise behind a
 *    spinner is indistinguishable from a broken feature.
 */
async function prepare(lang = DEFAULT_LANG, opts = {}) {
  const SR = ctor();
  if (!SR || typeof SR.install !== 'function') return 'unavailable';

  const state = await availability(lang);
  if (state === 'available') return 'available';

  if (state === 'downloadable') {
    try {
      await Promise.race([
        SR.install({ langs: [lang], processLocally: true }),
        sleep(opts.timeoutMs || 120000)
      ]);
    } catch {
      /* A refusal is reported by the availability check below. install() resolving
         false or throwing are the same outcome here, so neither is trusted. */
    }
  }

  return settle(lang, opts);
}

// Speech arrives as fragments with inconsistent leading spaces. Joining through
// one function keeps the caller from having to think about it, and collapsing
// runs of whitespace is safe here because the destination is a single-line input.
function join(a, b) {
  return `${a || ''} ${b || ''}`.replace(/\s+/g, ' ').trim();
}

// Module-level, because only one microphone session can be meaningful at a time
// and a second one would silently fight the first.
let session = null;

/**
 * Stop the active session. Safe to call when nothing is running.
 *
 * Teardown is NOT done here: setting `stopping` and calling stop() lets the
 * browser's own onend fire, which is the one place that finishes and notifies.
 * Two teardown paths would race, and the symptom would be a mic button stuck in
 * the recording state — the same failure speech.js guards against for read-aloud.
 */
function stop() {
  if (!session) return;
  session.stopping = true;
  clearTimeout(session.capTimer);
  // stop(), not abort(). Both close the microphone immediately, but stop() lets
  // the phrase in flight settle and deliver one last final result, so releasing
  // the key mid-word keeps the browser's best transcription of it rather than the
  // rougher interim the user happened to be looking at. abort() is the fallback
  // only because a session already torn down will throw on either.
  try {
    session.rec.stop();
  } catch {
    try {
      session.rec.abort();
    } catch {
      /* already gone — onend has run or the object is dead */
    }
  }
}

function isActive() {
  return !!session;
}

/**
 * Start dictating.
 *
 * onInterim(text) fires with the FULL text so far including the unsettled tail,
 * many times a second. onFinal(text) fires with the full text so far once a
 * phrase has settled. Both hand back the whole string rather than a delta so the
 * caller never has to accumulate — accumulating in two places is how the input
 * and the transcript drift apart.
 *
 * Returns false if it could not start, in which case no callback ever fires.
 */
function start({ lang = DEFAULT_LANG, onInterim, onFinal, onError, onEnd } = {}) {
  const SR = ctor();
  if (!SR) return false;
  if (session) stop();

  const state = { rec: null, stopping: false, restarts: 0, capTimer: null, settled: '' };

  const finish = () => {
    if (session === state) session = null;
    clearTimeout(state.capTimer);
    if (typeof onEnd === 'function') onEnd();
  };

  const spawn = () => {
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    // THE GUARANTEE. Never omitted, never false, never behind a condition.
    rec.processLocally = true;

    rec.onresult = (e) => {
      let tail = '';
      let added = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        const text = result[0] ? result[0].transcript : '';
        if (result.isFinal) added = join(added, text);
        else tail = join(tail, text);
      }
      if (added) {
        state.settled = join(state.settled, added);
        // Real speech resets the silence budget: three pauses spread over a long
        // dictation must not add up to an abandoned session.
        state.restarts = 0;
        if (typeof onFinal === 'function') onFinal(state.settled);
      }
      if (tail && typeof onInterim === 'function') onInterim(join(state.settled, tail));
    };

    rec.onerror = (e) => {
      const code = (e && e.error) || 'unknown';
      // Ordinary punctuation of a dictation session, not failures: 'no-speech' is
      // a quiet moment and 'aborted' is our own stop(). Surfacing either would
      // train the user to ignore the error state that does matter.
      if (code === 'no-speech' || code === 'aborted') return;
      state.stopping = true;
      if (typeof onError === 'function') onError(code);
    };

    rec.onend = () => {
      if (state.stopping || state.restarts >= MAX_SILENT_RESTARTS) return finish();
      state.restarts += 1;
      try {
        spawn();
      } catch {
        finish();
      }
    };

    state.rec = rec;
    rec.start();
  };

  // Assigned BEFORE start() so that a session which fails instantly still has its
  // onend handled by the code above rather than by nothing.
  session = state;
  state.capTimer = setTimeout(stop, MAX_SESSION_MS);

  try {
    spawn();
  } catch {
    clearTimeout(state.capTimer);
    session = null;
    return false;
  }
  return true;
}

export { isSupported, availability, prepare, settle, start, stop, isActive, DEFAULT_LANG, MAX_SESSION_MS };
